import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState, useCallback } from "react";
import { listLimpieza, listLimpiezaHistorial, cambiarEstadoLimpieza, type HabitacionLimpieza, type EstadoLimpieza, type LimpiezaHistorialItem } from "@/lib/limpieza.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, BedDouble, Wrench, AlertTriangle, Printer, History, Clock } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/gestion/limpieza")({
  component: LimpiezaPage,
});

const ESTADOS: { value: EstadoLimpieza; label: string; color: string; icon: any }[] = [
  { value: "limpia", label: "Limpia", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: Sparkles },
  { value: "sucia", label: "Sucia", color: "bg-amber-100 text-amber-900 border-amber-200", icon: BedDouble },
  { value: "mantenimiento", label: "Mantenimiento", color: "bg-rose-100 text-rose-900 border-rose-200", icon: Wrench },
];

const fmtRel = (iso: string) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  const rtf = new Intl.RelativeTimeFormat("es-ES", { numeric: "auto" });
  if (diff < 60) return rtf.format(-Math.round(diff), "second");
  if (diff < 3600) return rtf.format(-Math.round(diff / 60), "minute");
  if (diff < 86400) return rtf.format(-Math.round(diff / 3600), "hour");
  return rtf.format(-Math.round(diff / 86400), "day");
};

function HabitacionCard({ h, onChange }: { h: HabitacionLimpieza; onChange: () => void }) {
  const cambiar = useServerFn(cambiarEstadoLimpieza);
  const [nota, setNota] = useState(h.nota);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const meta = ESTADOS.find((e) => e.value === h.estado)!;

  async function setEstado(estado: EstadoLimpieza) {
    setLoading(true);
    try {
      await cambiar({ data: { habitacion_id: h.habitacion_id, estado, nota: estado === "mantenimiento" ? nota : "" } });
      toast.success(`${h.habitacion_nombre} → ${estado}`);
      setEditing(false);
      onChange();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className={`${h.prioridad && h.estado !== "limpia" ? "border-destructive ring-1 ring-destructive/30" : ""}`}>
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-medium leading-tight">{h.habitacion_nombre}</p>
            <p className="text-xs text-muted-foreground">Actualizado {fmtRel(h.cambiado_at)}</p>
          </div>
          <Badge variant="outline" className={meta.color}>
            <meta.icon className="mr-1 h-3 w-3" /> {meta.label}
          </Badge>
        </div>

        {h.prioridad && h.estado !== "limpia" && (
          <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
            <AlertTriangle className="h-3 w-3" /> Llegada hoy: {h.prox_huesped}
          </div>
        )}

        {!h.prioridad && h.proxima_llegada && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" /> Próxima llegada: {h.proxima_llegada} ({h.prox_huesped})
          </p>
        )}

        {h.estado === "sucia" && h.ultimo_huesped && (
          <p className="text-xs text-muted-foreground">Último huésped: {h.ultimo_huesped}</p>
        )}

        {h.nota && h.estado === "mantenimiento" && (
          <p className="rounded-md bg-rose-50 p-2 text-xs text-rose-900">🔧 {h.nota}</p>
        )}

        {editing ? (
          <div className="space-y-2">
            <Textarea rows={2} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Descripción de la incidencia…" />
            <div className="flex gap-2">
              <Button size="sm" disabled={loading} onClick={() => setEstado("mantenimiento")}>Confirmar</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancelar</Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 print:hidden">
            {h.estado !== "limpia" && <Button size="sm" disabled={loading} onClick={() => setEstado("limpia")}><Sparkles className="mr-1 h-3 w-3" /> Marcar limpia</Button>}
            {h.estado !== "sucia" && <Button size="sm" variant="outline" disabled={loading} onClick={() => setEstado("sucia")}><BedDouble className="mr-1 h-3 w-3" /> Sucia</Button>}
            {h.estado !== "mantenimiento" && <Button size="sm" variant="outline" disabled={loading} onClick={() => setEditing(true)}><Wrench className="mr-1 h-3 w-3" /> Mantenimiento</Button>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LimpiezaPage() {
  const fn = useServerFn(listLimpieza);
  const histFn = useServerFn(listLimpiezaHistorial);
  const [data, setData] = useState<HabitacionLimpieza[] | null>(null);
  const [hist, setHist] = useState<LimpiezaHistorialItem[]>([]);
  const [showHist, setShowHist] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    fn().then(setData).catch((e) => setErr(e?.message ?? "error"));
    if (showHist) histFn({ data: { limit: 50 } }).then(setHist).catch(() => {});
  }, [fn, histFn, showHist]);

  useEffect(() => { load(); }, [load]);

  if (err) return <p className="text-sm text-destructive">{err}</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Cargando habitaciones…</p>;

  const counts = {
    limpia: data.filter((d) => d.estado === "limpia").length,
    sucia: data.filter((d) => d.estado === "sucia").length,
    mantenimiento: data.filter((d) => d.estado === "mantenimiento").length,
    urgentes: data.filter((d) => d.prioridad && d.estado !== "limpia").length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3 print:hidden">
        <div>
          <h2 className="font-serif text-2xl">Limpieza y mantenimiento</h2>
          <p className="text-sm text-muted-foreground">Estado de cada habitación. Las que tienen llegada hoy están resaltadas.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowHist((s) => !s)}><History className="mr-1 h-4 w-4" /> {showHist ? "Ocultar historial" : "Ver historial"}</Button>
          <Button variant="outline" onClick={() => window.print()}><Printer className="mr-1 h-4 w-4" /> Imprimir</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground">Limpias</p><p className="font-serif text-2xl text-emerald-700">{counts.limpia}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground">Sucias</p><p className="font-serif text-2xl text-amber-700">{counts.sucia}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground">Mantenimiento</p><p className="font-serif text-2xl text-rose-700">{counts.mantenimiento}</p></CardContent></Card>
        <Card className={counts.urgentes > 0 ? "border-destructive" : ""}><CardContent className="p-4"><p className="text-xs uppercase text-muted-foreground">Urgentes (llegada hoy)</p><p className="font-serif text-2xl text-destructive">{counts.urgentes}</p></CardContent></Card>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.map((h) => <HabitacionCard key={h.habitacion_id} h={h} onChange={load} />)}
      </div>

      {showHist && (
        <Card>
          <CardHeader><CardTitle className="text-base">Historial reciente</CardTitle></CardHeader>
          <CardContent>
            {hist.length === 0 ? <p className="text-xs text-muted-foreground">Sin cambios registrados.</p> : (
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground">
                  <tr><th className="text-left">Cuándo</th><th className="text-left">Habitación</th><th className="text-left">Cambio</th><th className="text-left">Origen</th><th className="text-left">Nota</th></tr>
                </thead>
                <tbody>
                  {hist.map((h) => (
                    <tr key={h.id} className="border-t">
                      <td className="py-1.5">{fmtRel(h.created_at)}</td>
                      <td>{h.habitacion_nombre}</td>
                      <td>{h.estado_anterior ?? "—"} → <strong>{h.estado_nuevo}</strong></td>
                      <td><Badge variant="outline" className="text-xs">{h.origen}</Badge></td>
                      <td className="text-muted-foreground">{h.nota}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}